import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BlobPdfPreviewModal from '../../components/ui/BlobPdfPreviewModal';
import {
  fetchCaseScenarioPdf,
  listActiveCaseScenarios,
  type CaseScenario,
} from '../../services/caseScenarios';
import { setFlashToast } from '../../utils/flashToast';

function ScenarioIcon() {
  return (
    <div className="nc-ico ico-info" aria-hidden="true">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    </div>
  );
}

function PdfIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="12" y1="18" x2="12" y2="12" />
      <line x1="9" y1="15" x2="15" y2="15" />
    </svg>
  );
}

export default function CaseScenariosPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<CaseScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<{ fileName: string; title: string } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    void listActiveCaseScenarios(controller.signal).then((res) => {
      if (controller.signal.aborted) return;
      if (!res.success) {
        setFlashToast({ message: res.message ?? t('caseScenarios.loadFailed'), variant: 'error' });
        setItems([]);
        setLoading(false);
        return;
      }
      setItems(res.data);
      setLoading(false);
    });
    return () => controller.abort();
  }, [t]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  const closePreview = () => {
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(false);
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  };

  const openInAppPreview = (scenario: CaseScenario) => {
    const fileName = scenario.pdfFileName.trim();
    if (!fileName) {
      setFlashToast({ message: t('caseScenarios.pdfLoadFailed'), variant: 'error' });
      return;
    }

    closePreview();
    setPreview({ fileName, title: scenario.title });
    setPreviewLoading(true);

    void fetchCaseScenarioPdf(fileName).then((res) => {
      if (!res.success) {
        setPreviewError(res.message ?? t('caseScenarios.pdfLoadFailed'));
        setPreviewLoading(false);
        return;
      }
      const url = URL.createObjectURL(res.data);
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setPreviewLoading(false);
    });
  };

  return (
    <>
      <div className="bc">
        <span>{t('caseScenarios.breadcrumb')}</span>
        {preview ? (
          <>
            <span className="bc-sep">/</span>
            <span>{preview.title}</span>
          </>
        ) : null}
      </div>

      <div className="notif-toprow">
        <div className="notif-page-title">{t('caseScenarios.title')}</div>
      </div>

      <div className="nc-wrap">
        {loading ? (
          <div className="nc" style={{ cursor: 'default' }}>
            <div className="nc-content" style={{ color: 'var(--t3)', fontSize: 13 }}>
              {t('caseScenarios.loading')}
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="nc" style={{ cursor: 'default' }}>
            <div className="nc-content" style={{ color: 'var(--t3)', fontSize: 13 }}>
              {t('caseScenarios.none')}
            </div>
          </div>
        ) : (
          items.map((s) => (
            <div key={s.id} className="nc cs-scenario-row" style={{ cursor: 'default' }}>
              <ScenarioIcon />
              <div className="nc-content">
                <div className="nc-header">
                  <div className="nc-title">{s.title}</div>
                  {s.pdfFileName ? (
                    <Link
                      to={`/case-scenarios/pdf?file=${encodeURIComponent(s.pdfFileName)}&title=${encodeURIComponent(s.title)}`}
                      className="cs-pdf-link"
                      title={t('caseScenarios.viewPdf')}
                      aria-label={t('caseScenarios.viewPdf')}
                      onClick={(e) => {
                        // Left-click → in-app viewer; keep href for right-click "Open in new tab".
                        e.preventDefault();
                        openInAppPreview(s);
                      }}
                    >
                      <PdfIcon />
                    </Link>
                  ) : null}
                </div>
                {s.description ? <div className="nc-body">{s.description}</div> : null}
              </div>
            </div>
          ))
        )}
      </div>

      <BlobPdfPreviewModal
        isOpen={Boolean(preview)}
        title={preview?.title ?? ''}
        objectUrl={previewUrl}
        loading={previewLoading}
        error={previewError}
        onClose={closePreview}
      />
    </>
  );
}
